/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import { useGlobalSearch, useProcess } from "@/global-data";
import { useDeletePopup } from "@/global-data/use-delete-popup";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { getUserInfo } from "@/lib/token";
import { wait } from "@/lib/wait";
import { apiServices } from "@/services/api.services";
import { useEffect, useMemo, useState } from "react";
import { companies, jobId, roles, users } from "@/services/global.services";
import { useToast } from "@/hooks/use-toast";
import { GenericType } from "@/types";
import { useUserInfo } from "@/global-data/use-user-info";
import { storeUser } from "@/pages/login/toolkit/utils.lib";
import { getJobId } from "@/lib/utils";

export const useMutations = () => {
  const API_SERVICE = useMemo(() => {
    return roles;
  }, [roles]);

  const { setUser } = useUserInfo();

  const { toast } = useToast();

  const { user_id, company_id } = useMemo(() => {
    const tempUserData = getUserInfo();
    return {
      company_id: tempUserData?.company_id,
      user_id: tempUserData?.user_id || "",
    };
  }, []);

  const { setProcess } = useProcess();
  const [skeltonLoading, setSkeltonLoading] = useState(true);
  const [listData, setListData] = useState([]);
  // const [companyList, setCompanyList] = useState<GenericType>([]);
  const [formPopupOpen, setFormPopupOpen] = useState(false);
  const [activeRecordData, setActiveRecordData] =
    useState<GenericType>(undefined);
  const [tableColumn, setTableColumn] = useState([]);
  const [deleteId, setDeleteId] = useState(undefined);
  const [currentUser, setCurrentUser] = useState(undefined);
  const [companyData, setCompanyData] = useState(undefined);

  const { setSearchProgress, search } = useGlobalSearch();
  const [searchDebounced] = useDebouncedValue(search, 200);

  const { onDelete, status, identity, clearDelete } = useDeletePopup();
  const [filter, setFilter] = useState({});

  const [paginationData, setPaginationData] = useState({
    page: 1,
    perPage: 10,
    totalItem: 1,
  });

  //On Load Component
  useEffect(() => {
    (async () => {
      await Promise.all([
        loadingList({ searchText: "" }),
        loadUser(),
        loadingCompany(),
      ]);
      // await loadingList({ searchText: "" });
      // await loadUser();
      // await loadingCompany();
      await wait(1000);
      setSkeltonLoading(false);
      setProcess(false);
    })();
  }, []);

  const loadingCompany = async () => {
    // const userData: any = getUserInfo();
    const payload = {
      data: {
        filter_by: {
          // company_id: [currentUserData.company_id],
          search_text: "",
          exact_match: false,
        },
      },
      company_id: company_id,
      user_id: company_id === "system" ? "" : user_id,
    };
    const { data, error, message /* error, message, statusCode */ } =
      await apiServices(companies.get, "post", payload);

    if (data) {
      const companyList = data.result.metadata.company.map((item: any) => ({
        company_id: item.company_id,
        company_name: item.display_name,
      }));

      setCompanyData(companyList);
    }
    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }

    return { data, error };
  };

  const loadUser = async () => {
    const payload = {
      data: {
        user_id: user_id,
        filter_by: {
          company_id: [company_id],
          search_text: "",
          exact_match: false,
        },
      },
      company_id: company_id,
      user_id: user_id,
    };
    const { data, error, message } = await apiServices(
      users.get,
      "post",
      payload,
    );

    if (data?.result?.metadata?.users?.[0]) {
      setCurrentUser(data.result?.metadata?.users[0]);
    }
    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    return { data: data?.result?.metadata?.users?.[0], error };
  };

  //handle delete
  useEffect(() => {
    if (status === "cancel") {
      setDeleteId(undefined);
      clearDelete();
    }
    if (status === "delete" && identity === "user") {
      (async () => {
        setProcess(true);
        const deleteResponse = await deleteRecord();
        console.log({ deleteResponse });
        console.log({ code: deleteResponse?.data?.code });
        if (deleteResponse.data) {
          if (deleteResponse.data?.job_id) {
            const jobIdResponse = await getJobId(deleteResponse.data?.job_id);
            if (jobIdResponse.data) {
              await loadingList({});

              toast({
                title: "Success",
                variant: "success",
                description: jobIdResponse.data.result.metadata.message,
              });
            }
          } else if (deleteResponse?.data?.code) {
            const getResponse = await loadingList({});
            if (getResponse.data) {
              toast({
                title: "Success",
                variant: "success",
                description: "Role is deleted successfully",
              });
            }
          }
        }
        clearDelete();
        setDeleteId(undefined);
        setProcess(false);
        setActiveRecordData(null);
      })();
    }
  }, [status]);

  const deleteRecord = async () => {
    const payload = {
      data: {
        role_id: deleteId,
      },
      company_id: company_id,
    };
    const { data, error, message } = await apiServices<GenericType>(
      API_SERVICE.delete,
      "post",
      payload,
    );

    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    return { data, error };
  };

  //Handle Search
  useEffect(() => {
    if (skeltonLoading === false) {
      (async () => {
        setSearchProgress(true);
        await loadingList({});
        setSearchProgress(false);
      })();
    }
  }, [searchDebounced]);

  const onFormOpenChange = (prop: boolean) => {
    setFormPopupOpen(prop);
  };

  const onPageChange = async (prop: number) => {
    setPaginationData((prev) => ({ ...prev, page: prop }));
    setProcess(true);
    await loadingList({ page: prop });
    setProcess(false);
  };
  const onPerPageChange = async (prop: number) => {
    setPaginationData((prev) => ({ ...prev, perPage: prop, page: 1 }));
    setProcess(true);
    await loadingList({ perPage: prop, page: 1 });
    setProcess(false);
  };

  const permission: GenericType = [];
  const onAction = async ({ action, data }: GenericType) => {
    if (action === "edit") {
      setActiveRecordData(data);
      onFormOpenChange(true);
    }
    if (action === "delete") {
      setActiveRecordData(data);
      setDeleteId(data.role_id);
      onDelete({
        description:
          "This action cannot be undone. This will permanently delete the record and remove its data from our system.",
        id: data.user_id,
        identity: "user",
        popupStatus: true,
        title: "Are you sure you want to delete this Role?",
      });
      await loadingList({});
    }
  };

  useEffect(() => {
    if (formPopupOpen === false) {
      setActiveRecordData(undefined);
    }
  }, [formPopupOpen]);

  const loadingList = async ({
    perPage,
    page,
    filterReset,
    makeFilter,
    searchText,
  }: {
    perPage?: number;
    page?: number;
    filterReset?: boolean;
    makeFilter?: boolean;
    searchText?: string;
  }) => {
    let apiFilter = {};

    if (!filterReset) {
      apiFilter = filter;
    }

    let page_no = page || paginationData.page;

    if (makeFilter) {
      page_no = 1;
    }

    const payload = {
      data: {
        filter_by: {
          ...apiFilter,
          search_text: searchText ?? searchDebounced,
          exact_match: false,
          pagination: {
            page_no: page_no,
            no_of_recs: perPage || paginationData.perPage,
            sort_by: "cts",
            order_by: false,
          },
        },
      },
      company_id: company_id,
      // company_id: company_id === "system" ? "test97" : company_id,
      user_id: user_id,
    };
    const { data, error, message } = await apiServices<GenericType>(
      API_SERVICE.get,
      "post",
      payload,
    );

    if (data) {
      setListData(data.result.metadata[API_SERVICE.key]);
      setPaginationData((prev) => ({
        ...prev,
        totalItem: data?.result?.metadata?.total_records || 1,
      }));
    }
    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    return { data, error };
  };

  // const getJobId = async (prop: string) => {
  //   const { data, error, message } = await apiServices<GenericType>(
  //     jobId.get + prop,
  //     "get",
  //   );

  //   if (data?.process_status === "in-progress") {
  //     await new Promise((resolve) => setTimeout(resolve, 5000)); // Wait 5 seconds before retrying

  //     const jobResponse: GenericType = await getJobId(prop);
  //     return jobResponse;
  //   }
  //   if (data?.status?.error_details?.fail_message) {
  //     toast({
  //       title: "Error",
  //       variant: "destructive",
  //       description: data.status.error_details.fail_message,
  //     });
  //     return { data: null, error: true };
  //   }
  //   if (error) {
  //     toast({
  //       title: "Error",
  //       variant: "destructive",
  //       description: error.status?.message || message,
  //     });
  //   }
  //   return { data, error };
  // };

  const onFormSubmit = async (prop: GenericType): Promise<GenericType> => {
    let payloadData = {};
    console.log({ prop });
    if (activeRecordData?.role_id) {
      payloadData = {
        role_name: prop.role_name,
        description: prop.description,
        permission: prop.permission,
        role_id: activeRecordData.role_id,
        company_id: company_id,
      };
    } else {
      payloadData = {
        role_name: prop.role_name,
        description: prop.description,
        permission: prop.permission,
        company_id: company_id,
      };
    }
    const payload = {
      data: payloadData,
      company_id: company_id,
      user_id: user_id,
    };

    const { data, error } = await apiServices<GenericType>(
      API_SERVICE.post,
      "post",
      payload,
    );

    if (data?.job_id) {
      const jobIdResponse = await getJobId(data.job_id);
      if (jobIdResponse.error) {
        return jobIdResponse;
      }
      const getResponse = await loadingList({});
      if (getResponse.error) {
        return getResponse;
      }
      if (getResponse.data) {
        toast({
          title: "Success",
          variant: "success",
          description:
            jobIdResponse?.data?.result?.metadata?.message ??
            jobIdResponse?.data?.status?.msg ??
            jobIdResponse?.data?.status?.message,
        });
      }
    } else if (data?.result?.metadata?.message) {
      const getUserResponse = await loadUser();
      if (getUserResponse.error) return null;
      const getResponse = await loadingList({});
      if (getResponse.error) return null;
      setUser(getUserResponse.data);
      storeUser(getUserResponse.data);
      toast({
        title: "Success",
        variant: "success",
        description:
          data?.result?.metadata?.message ??
          data?.status?.msg ??
          data?.status?.message,
      });
    }
    if (error) {
      setProcess(false);
    }
    return { data, error };
  };

  const filterRequest = async () => {
    setProcess(true);
    await loadingList({ makeFilter: true });
    setProcess(false);
  };
  const clearFilter = async () => {
    setFilter({});
    setProcess(true);
    await loadingList({ filterReset: true });
    setProcess(false);
  };

  return {
    skeltonLoading,
    listData,
    onPageChange,
    onPerPageChange,
    paginationData,
    permission,
    onAction,
    activeRecordData,
    onFormSubmit,
    onFormOpenChange,
    formPopupOpen,
    tableColumn,
    setTableColumn,
    // companyList,
    filter,
    setFilter,
    filterRequest,
    clearFilter,
    currentUser,
    companyData,
    company_id,
  };
};
